import { z } from 'zod';
/**
 * Plattform-Administratoren — die Mitglieder der Cognito-Gruppe `SystemAdmin`.
 *
 * ### Warum es das jetzt gibt, obwohl es das bewusst nicht gab
 *
 * Bis zum 26.08.2026 wurde die Mitgliedschaft ausschließlich out-of-band vergeben, über
 * `scripts/create-admin-user.mjs` mit AWS-Zugangsdaten. Die Begründung stand im Skript und im
 * `auth-stack.ts` und war gut: **Ein kompromittiertes Admin-Portal soll keine neuen
 * Plattform-Admins erzeugen können.**
 *
 * Sie hatte nur einen praktischen Preis, der mit dem zweiten Menschen im Betrieb fällig wird:
 * Einen Kollegen zu berechtigen setzt AWS-Zugangsdaten, ein Terminal und das Repository
 * voraus. Wer das nicht hat, wartet — und in der Zwischenzeit teilen sich zwei Personen ein
 * Konto. Das ist der schlechtere Zustand: ein geteiltes Passwort, ein Protokoll, in dem alle
 * Eingriffe demselben Namen zugeordnet sind, und kein Weg, jemandem den Zugang wieder zu
 * nehmen.
 *
 * ### Was an die Stelle der alten Sperre tritt
 *
 * Die Fähigkeit ist jetzt im Portal, die Absicherung dafür in vier Schichten:
 *
 * | Schicht                            | Wogegen                                              |
 * | ---------------------------------- | ----------------------------------------------------- |
 * | `assertAdmin()` serverseitig       | Jeder Aufruf ohne `SystemAdmin`                       |
 * | Kein Passwort im Vorgang           | Der Angelegte setzt es selbst über den Reset-Weg      |
 * | Mail an die Betreiberadresse       | Ein stiller Missbrauch — jede Änderung ist sichtbar   |
 * | Protokolleintrag (`internal`)      | Die Frage „wer hat wen wann berechtigt?"              |
 *
 * Dazu zwei Sperren gegen den Selbstausschluss: **Niemand kann sich selbst entfernen**, und
 * **der letzte Administrator bleibt**. Beides ist keine Theorie — ein Pool ohne Mitglied in
 * dieser Gruppe ist genau der Zustand, in dem in Atleo niemand mehr an die Administration kam.
 *
 * Das CLI-Skript bleibt trotzdem: Es ist der Weg zurück, wenn niemand mehr hineinkommt.
 */
/** Der Zustand eines Cognito-Kontos, soweit die Admin-Liste ihn zeigen soll. */
export const PlatformAdminStatusSchema = z.enum([
    /** Angelegt und bestätigt — kann sich anmelden, sobald ein Passwort gesetzt ist. */
    'CONFIRMED',
    /** Angelegt, Passwort noch nicht selbst vergeben. */
    'RESET_REQUIRED',
    /** Cognito-Zustand nach `AdminCreateUser` mit temporärem Passwort. */
    'FORCE_CHANGE_PASSWORD',
    /** Alles andere, was Cognito melden kann. Ungefiltert durchgereicht statt verschluckt. */
    'UNKNOWN',
]);
export const PLATFORM_ADMIN_STATUS_LABELS = {
    CONFIRMED: 'Aktiv',
    RESET_REQUIRED: 'Passwort ausstehend',
    FORCE_CHANGE_PASSWORD: 'Passwort ausstehend',
    UNKNOWN: 'Unbekannt',
};
/** Eine Zeile der Administratorenliste. */
export const PlatformAdminSchema = z.object({
    /** Der Cognito-`Username`. Bei diesem Pool die E-Mail-Adresse, aber nicht per Definition. */
    username: z.string().min(1),
    email: z.string(),
    status: PlatformAdminStatusSchema,
    /** Ob das Konto aktiviert ist. Ein deaktiviertes kann sich nicht anmelden. */
    enabled: z.boolean(),
    createdAt: z.iso.datetime().nullable(),
    /**
     * Ob das der Aufrufer selbst ist.
     *
     * Serverseitig gesetzt und nicht im Frontend verglichen: Die Oberfläche blendet damit den
     * Entfernen-Knopf aus, und der Server lehnt denselben Aufruf ohnehin ab. Ein Vergleich im
     * Browser über die E-Mail-Adresse wäre die dritte Stelle, an der dieselbe Frage anders
     * beantwortet werden könnte.
     */
    isSelf: z.boolean(),
});
/**
 * Was beim Anlegen eines Administrators angegeben wird — **kein Passwort**.
 *
 * Der Angelegte vergibt es selbst über den Weg „Passwort vergessen", mit einem Code, den
 * Cognito ihm schickt. Ein Passwort, das jemand anderes gesetzt hat und über irgendeinen Kanal
 * weitergibt, wäre bei einem Konto mit Vollzugriff auf alle Stamm- und Kundendaten die
 * schlechteste aller Varianten — und der Grund, aus dem das CLI-Skript es genau einmal auf der
 * Konsole zeigt und nirgends speichert.
 */
export const PlatformAdminCreateSchema = z.object({
    email: z.email().max(254),
    /** Nur für die Anrede in der Einladungsmail. Optional. */
    name: z.string().max(200).default(''),
});
